import type { ReactNode } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";

/**
 * Piezas del panel de métricas. Todas las series son de una sola variable, así
 * que no llevan leyenda: el título del bloque nombra lo que se está mirando.
 */

export function MetricCard({
  label,
  value,
  hint,
  tone = "default",
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "default" | "good" | "warning" | "critical";
}) {
  const toneClass = {
    default: "text-foreground",
    good: "text-emerald-600 dark:text-emerald-400",
    warning: "text-amber-600 dark:text-amber-400",
    critical: "text-red-600 dark:text-red-400",
  }[tone];

  return (
    <div className="rounded-2xl border bg-card p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${toneClass}`}>
        {value}
      </p>
      {hint && (
        <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

export function MetricsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </section>
  );
}

function ChartTooltip({
  active,
  payload,
  label,
  format,
}: {
  active?: boolean;
  payload?: any[];
  label?: any;
  format: (value: number) => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-sm shadow-lg">
      <p className="font-medium">{dayjs(label).format("DD MMM YYYY")}</p>
      <p className="text-muted-foreground">
        {format(Number(payload[0]?.value ?? 0))}
      </p>
    </div>
  );
}

/** Serie diaria única (registros, activos, costo…). */
export function DailyTrendChart({
  data,
  dataKey,
  color,
  format = (v) => String(v),
}: {
  data: Array<Record<string, any>>;
  dataKey: string;
  color: string;
  format?: (value: number) => string;
}) {
  return (
    <div className="h-[220px] w-full rounded-2xl border bg-card p-4">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={`fill-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.25} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            className="stroke-border"
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tickFormatter={(value) => dayjs(value).format("DD MMM")}
            tickLine={false}
            axisLine={false}
            className="text-xs fill-muted-foreground"
            minTickGap={24}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={40}
            className="text-xs fill-muted-foreground"
            allowDecimals={false}
          />
          <Tooltip content={<ChartTooltip format={format} />} />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={2}
            fill={`url(#fill-${dataKey})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Embudo de conversión: barras horizontales proporcionales al primer escalón,
 * con el porcentaje de retención respecto al paso anterior.
 */
export function FunnelBars({
  steps,
}: {
  steps: { label: string; value: number }[];
}) {
  const top = steps[0]?.value ?? 0;

  return (
    <div className="flex flex-col gap-3 rounded-2xl border bg-card p-4">
      {steps.map((step, index) => {
        const previous = index === 0 ? null : steps[index - 1]!.value;
        const share = top > 0 ? (step.value / top) * 100 : 0;
        const retention =
          previous && previous > 0
            ? Math.round((step.value / previous) * 100)
            : null;

        return (
          <div key={step.label} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between text-sm">
              <span className="font-medium">{step.label}</span>
              <span className="tabular-nums text-muted-foreground">
                {step.value}
                {retention !== null && ` · ${retention}% del paso anterior`}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${Math.max(share, step.value > 0 ? 2 : 0)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
